import { normalizeRole, getDashboardRoute } from './utils/dashboardRoute';
import EmailVerificationGate from './components/EmailVerificationGate';
import React, { useState, useEffect, Component, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css';
import ChatBubble from './components/ChatBubble';
import BrandLoader, { waitBrandIntro } from './components/BrandLoader';
import FeedbackModal from './components/FeedbackModal';
import { ENV_CONFIG } from './config/env';
import { getStoredUser, clearLocalSession } from './api/session';
import { unifiedRolesOf } from './pages/Unified/unifiedMenu';
import { DASHBOARD_LOADERS, preloadDashboard } from './pages/dashboardChunks';
const TenantDashboard = lazy(DASHBOARD_LOADERS.tenant);
const AdministrativeDashboard = lazy(DASHBOARD_LOADERS.admin);
const AccountingDashboard = lazy(DASHBOARD_LOADERS.accounting);
const SalesManagerDashboard = lazy(DASHBOARD_LOADERS.salesmanager);
const TechnicianDashboard = lazy(DASHBOARD_LOADERS.technician);
const LandlordDashboard = lazy(DASHBOARD_LOADERS.landlord);
const SystemDashboard = lazy(() => import('./pages/SystemDashboard'));
const SuperAdminDashboard = lazy(DASHBOARD_LOADERS.superadmin);
const AgencyDirectorDashboard = lazy(DASHBOARD_LOADERS.agency_director);
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const ReceiptPage = lazy(() => import('./pages/ReceiptPage'));
const RegisterAgencyPage = lazy(() => import('./pages/RegisterAgencyPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/Legal/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/Legal/TermsPage'));
const LegalNoticePage = lazy(() => import('./pages/Legal/LegalNoticePage'));
const SeoLandingPage = lazy(() => import('./pages/SeoLandingPage'));
const UnifiedDashboard = lazy(DASHBOARD_LOADERS.unified);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});

if (typeof window !== 'undefined') {
  window.__hubimmoQueryClient = queryClient;
}

class AppErrorBoundary extends Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('App error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'sans-serif' }}>
          <h1>Une erreur est survenue</h1>
          <p>{this.state.error?.message || 'Please refresh the page or try again later.'}</p>
          <button type="button" onClick={() => window.location.reload()} style={{ padding: '8px 16px', marginTop: '12px', cursor: 'pointer' }}>
            Recharger la page
          </button>
        </div>);

    }
    return this.props.children;
  }
}

const DashboardLayout = ({ children }) => (
  <div className="dashboard-layout">
    <div className="main-content">
      {children}
    </div>
    <ChatBubble />
  </div>
);

const ProtectedRoute = ({ children, requiredRole }) => {
  // La session est validée au démarrage par /auth/me (cookie HttpOnly) : l'utilisateur stocké suffit ici.
  const effectiveUser = getStoredUser();

  if (!effectiveUser) {
    return <Navigate to="/login" replace />;
  }

  // Rôles cumulés selon l'offre (Solo : un compte accède à tous les espaces ; Pro : le manager cumule)
  const userRoles = (Array.isArray(effectiveUser.roles) && effectiveUser.roles.length ? effectiveUser.roles : [effectiveUser.role]).map(normalizeRole);
  if (requiredRole && effectiveUser.role && !userRoles.includes(normalizeRole(requiredRole))) {
    return <Navigate to={getDashboardRoute(effectiveUser.role)} replace />;
  }

  // Compte qui cumule plusieurs fonctions (Solo, manager Pro) : un seul tableau de bord combiné,
  // à l'adresse de sa fonction principale
  if (requiredRole && unifiedRolesOf(effectiveUser).length > 1 && unifiedRolesOf(effectiveUser).includes(normalizeRole(requiredRole))) {
    const home = getDashboardRoute(effectiveUser.role);
    if (window.location.pathname !== home) return <Navigate to={home} replace />;
    return <UnifiedDashboard />;
  }

  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Session : cookie HttpOnly (ajouté par l'intercepteur fetch) ou jeton historique s'il existe encore.
    const legacyToken = localStorage.getItem('token');
    fetch(`${ENV_CONFIG.API_BASE_URL}/api/v1/auth/me`, {
      headers: legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {}
    })
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Session expirée');
      })
      .then(body => {
        const u = body.data?.user || body.data;
        if (u) {
          setUser(u);
          localStorage.setItem('user', JSON.stringify(u));
          // Page ouverte directement sur le tableau de bord : son code est chargé sous le même écran de chargement
          // (un seul rond, au lieu d'un second dans le tableau de bord)
          if (window.location.pathname === getDashboardRoute(u.role)) return preloadDashboard(u);
        } else throw new Error('Session invalide');
      })
      .catch(() => {
        clearLocalSession();
        setUser(null);
      })
      // Écran de chargement : le temps que le nom s'écrive en entier, plus si la session ou le tableau de bord tardent
      .finally(() => waitBrandIntro(window.__hubBootAt).then(() => setLoading(false)));
  }, []);

  useEffect(() => {
    const handleLogout = () => {
      clearLocalSession();
      queryClient.clear();
      setUser(null);
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // Connexion réussie depuis la fenêtre de connexion
  useEffect(() => {
    const onLogin = (e) => { if (e.detail) setUser(e.detail); };
    window.addEventListener('auth:login', onLogin);
    return () => window.removeEventListener('auth:login', onLogin);
  }, []);



  if (loading) {
    return <BrandLoader />;
  }

  const effectiveUser = user || (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();

  return (
    <QueryClientProvider client={queryClient}>
    <AppErrorBoundary>
    <Router>
      <div className="App">
        <EmailVerificationGate />
        <Suspense fallback={<BrandLoader />}>
        <Routes>
          {/* Official Public Homepage */}
          <Route path="/" element={<HomePage />} />
          <Route path="/home" element={<Navigate to="/" replace />} />

          {/* Dedicated Login Route with auto-redirect if authenticated */}
          <Route
            path="/login"
            element={
              (() => {
                if (effectiveUser) return <Navigate to={getDashboardRoute(effectiveUser.role)} replace />;
                // Connexion en fenêtre modale par-dessus l'accueil (ouverte par l'en-tête public)
                return <HomePage />;
              })()
            }
          />

          {/* Public Property Marketplace */}
          {/* Ancienne page publique d'annonces : supprimée (le Marketplace sera un onglet des tableaux de bord) */}
          <Route path="/marketplace" element={<Navigate to="/" replace />} />

          {/* Legal Compliance Pages */}
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/legal" element={<LegalNoticePage />} />

          {/* Targeted SEO Landing Pages */}
          <Route path="/logiciel-agence-immobiliere" element={<SeoLandingPage intent="logiciel-agence-immobiliere" />} />
          <Route path="/gestion-locative" element={<SeoLandingPage intent="gestion-locative" />} />
          <Route path="/gestion-des-loyers" element={<SeoLandingPage intent="gestion-des-loyers" />} />
          <Route path="/suivi-des-impayes" element={<SeoLandingPage intent="suivi-des-impayes" />} />
          <Route path="/gestion-proprietaires" element={<SeoLandingPage intent="gestion-proprietaires" />} />
          <Route path="/logiciel-immobilier-cote-divoire" element={<SeoLandingPage intent="logiciel-immobilier-cote-divoire" />} />

          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/register" element={<RegisterAgencyPage />} />
          <Route path="/verify-email" element={<RegisterAgencyPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/receipt" element={<ReceiptPage />} />
          <Route path="/tenant" element={
                <ProtectedRoute requiredRole="tenant">
              <DashboardLayout>
                <TenantDashboard />
              </DashboardLayout>
            </ProtectedRoute>
                } />
          
          <Route path="/administrative" element={
                <ProtectedRoute requiredRole="admin">
              <DashboardLayout>
                <AdministrativeDashboard />
              </DashboardLayout>
            </ProtectedRoute>
                } />
          
          <Route path="/accounting" element={
                <ProtectedRoute requiredRole="accounting">
              <DashboardLayout>
                <AccountingDashboard />
              </DashboardLayout>
            </ProtectedRoute>
                } />
          
          <Route path="/sales-manager" element={
                <ProtectedRoute requiredRole="salesmanager">
              <DashboardLayout>
                <SalesManagerDashboard />
              </DashboardLayout>
            </ProtectedRoute>
                } />
          
          <Route path="/technician" element={
                <ProtectedRoute requiredRole="technician">
              <DashboardLayout>
                <TechnicianDashboard />
              </DashboardLayout>
            </ProtectedRoute>
                } />
          
          <Route path="/landlord" element={
                <ProtectedRoute requiredRole="landlord">
              <DashboardLayout>
                <LandlordDashboard />
              </DashboardLayout>
            </ProtectedRoute>
                } />
          
          <Route path="/system" element={
                <ProtectedRoute requiredRole="superadmin">
              <DashboardLayout>
                <SystemDashboard />
              </DashboardLayout>
            </ProtectedRoute>
                } />
          
          <Route path="/super-admin" element={
                <ProtectedRoute requiredRole="superadmin">
              <DashboardLayout>
                <SuperAdminDashboard />
              </DashboardLayout>
            </ProtectedRoute>
                } />

          <Route path="/agency-director" element={
                <ProtectedRoute requiredRole="agency_director">
              <DashboardLayout>
                <AgencyDirectorDashboard />
              </DashboardLayout>
            </ProtectedRoute>
                } />

          <Route path="/settings" element={
                <ProtectedRoute>
              <DashboardLayout>
                <SettingsPage />
              </DashboardLayout>
            </ProtectedRoute>
                } />

          <Route path="/subscription" element={
                <ProtectedRoute requiredRole="agency_director">
              <DashboardLayout>
                <SettingsPage initialTab="subscription" />
              </DashboardLayout>
            </ProtectedRoute>
                } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        <FeedbackModal />
      </div>
    </Router>
    </AppErrorBoundary>
    </QueryClientProvider>);

}

export default App;
